import React, { useState, useEffect } from 'react';
import { 
  Globe, 
  Save, 
  Plus, 
  Trash2, 
  CheckCircle, 
  Mail, 
  Phone, 
  MapPin, 
  Clock, 
  Share2, 
  CreditCard,
  ExternalLink
} from 'lucide-react';
import { AdminApiClient } from '../../services/adminApiClient';

interface LinkItem {
  id: string;
  label: string;
  url: string;
}

export interface SocialLinkItem {
  id: string;
  platform: 'instagram' | 'x' | 'facebook' | 'youtube' | 'linkedin' | 'tiktok' | 'whatsapp';
  url: string;
  enabled: boolean;
}

export const AVAILABLE_PLATFORMS: { key: SocialLinkItem['platform']; label: string; defaultUrl: string; color: string }[] = [
  { key: 'instagram', label: 'Instagram', defaultUrl: 'https://instagram.com/reuseitng', color: '#E1306C' },
  { key: 'x', label: 'X (Twitter)', defaultUrl: 'https://x.com/reuseitng', color: '#FFFFFF' },
  { key: 'facebook', label: 'Facebook', defaultUrl: 'https://facebook.com/reuseitng', color: '#1877F2' },
  { key: 'youtube', label: 'YouTube', defaultUrl: 'https://youtube.com/@reuseitng', color: '#FF0000' },
  { key: 'linkedin', label: 'LinkedIn', defaultUrl: 'https://linkedin.com/company/reuseit', color: '#0A66C2' },
  { key: 'tiktok', label: 'TikTok', defaultUrl: 'https://tiktok.com/@reuseitng', color: '#00F2FE' },
  { key: 'whatsapp', label: 'WhatsApp', defaultUrl: 'https://wa.me/2348007387348', color: '#25D366' },
];

export const FooterSettingsManager: React.FC = () => {
  const [isSaved, setIsSaved] = useState(false);

  // Newsletter settings
  const [newsletterTitle, setNewsletterTitle] = useState('NEW TO REUSEIT?');
  const [newsletterDesc, setNewsletterDesc] = useState(
    'Subscribe to our newsletter to get updates on the latest eco-friendly marketplace deals, sustainable living tips, and exclusive escrow promotions.'
  );
  const [newsletterConsent, setNewsletterConsent] = useState(
    'I agree to receive eco-shopping updates from ReUseIt in line with the Privacy Policy.'
  );

  // Column 1: Help links
  const [helpLinks, setHelpLinks] = useState<LinkItem[]>([
    { id: '1', label: 'Chat on WhatsApp Support', url: 'https://wa.me/2349012345678' },
    { id: '2', label: 'Help Center & FAQs', url: '/help' },
    { id: '3', label: 'How ReUseIt Escrow Works', url: '/how-it-works' },
    { id: '4', label: 'Dispute Resolution & Refunds', url: '/disputes' },
    { id: '5', label: 'Report a Product or Vendor', url: '/report' },
    { id: '6', label: 'Safe Trading Guidelines', url: '/safety' },
  ]);

  // Column 2: About links
  const [aboutLinks, setAboutLinks] = useState<LinkItem[]>([
    { id: '1', label: 'About Us & Circular Mission', url: '/about' },
    { id: '2', label: 'Carbon Impact & Tree Planting', url: '/impact' },
    { id: '3', label: 'ReUseIt Eco Leaderboard', url: '/leaderboard' },
    { id: '4', label: 'Terms & Conditions of Service', url: '/terms' },
    { id: '5', label: 'Privacy Policy (NDPA Compliant)', url: '/privacy' },
    { id: '6', label: 'Become a Verified Partner', url: '/partner' },
  ]);

  // Column 3: Contact info
  const [address, setAddress] = useState('14 Adeola Odeku Street, Victoria Island, Lagos, Nigeria');
  const [phone, setPhone] = useState('+234 800 REUSEIT (0800 738 7348)');
  const [whatsapp, setWhatsapp] = useState('+234 901 234 5678');
  const [email, setEmail] = useState('support@reuseit.ng');
  const [hours, setHours] = useState('Mon - Sat: 8:00 AM - 8:00 PM WAT');

  // Column 4: Dynamic Social links
  const [socialLinks, setSocialLinks] = useState<SocialLinkItem[]>([
    { id: '1', platform: 'instagram', url: 'https://instagram.com/reuseitng', enabled: true },
    { id: '2', platform: 'x', url: 'https://x.com/reuseitng', enabled: true },
    { id: '3', platform: 'facebook', url: 'https://facebook.com/reuseitng', enabled: true },
    { id: '4', platform: 'youtube', url: 'https://youtube.com/@reuseitng', enabled: true },
    { id: '5', platform: 'linkedin', url: 'https://linkedin.com/company/reuseit', enabled: true },
    { id: '6', platform: 'tiktok', url: 'https://tiktok.com/@reuseitng', enabled: true },
    { id: '7', platform: 'whatsapp', url: 'https://wa.me/2348007387348', enabled: true },
  ]);
  const [playStore, setPlayStore] = useState('https://play.google.com/store/apps/details?id=com.reuseit.app');
  const [appStore, setAppStore] = useState('https://apps.apple.com/app/reuseit/id123456789');

  // Payment methods
  const [payments, setPayments] = useState<Record<string, boolean>>({
    'VISA': true,
    'Mastercard': true,
    'Verve': true,
    'Bank Transfer (NIBSS)': true,
    'Flutterwave': true,
    'Paystack': true,
  });

  const [copyrightText, setCopyrightText] = useState(
    '© 2026 ReUseIt Nigeria Limited. All rights reserved. • Escrow Security Guaranteed.'
  );

  useEffect(() => {
    AdminApiClient.getPublicConfig()
      .then(config => {
        const saved = config?.configs?.footer_settings || config?.settings?.footer_settings;
        if (saved) {
          if (saved.newsletterTitle) setNewsletterTitle(saved.newsletterTitle);
          if (saved.newsletterDesc) setNewsletterDesc(saved.newsletterDesc);
          if (saved.newsletterConsent) setNewsletterConsent(saved.newsletterConsent);
          if (saved.helpLinks) setHelpLinks(saved.helpLinks);
          if (saved.aboutLinks) setAboutLinks(saved.aboutLinks);
          if (saved.address) setAddress(saved.address);
          if (saved.phone) setPhone(saved.phone);
          if (saved.whatsapp) setWhatsapp(saved.whatsapp);
          if (saved.email) setEmail(saved.email);
          if (saved.hours) setHours(saved.hours);
          if (saved.playStore) setPlayStore(saved.playStore);
          if (saved.appStore) setAppStore(saved.appStore);
          if (saved.payments) setPayments(saved.payments);
          if (saved.copyrightText) setCopyrightText(saved.copyrightText);

          if (Array.isArray(saved.socialLinks) && saved.socialLinks.length > 0) {
            setSocialLinks(saved.socialLinks);
          } else {
            const synth: SocialLinkItem[] = [];
            if (saved.instagram) synth.push({ id: '1', platform: 'instagram', url: saved.instagram, enabled: true });
            if (saved.twitter || saved.x) synth.push({ id: '2', platform: 'x', url: saved.twitter || saved.x, enabled: true });
            if (saved.facebook) synth.push({ id: '3', platform: 'facebook', url: saved.facebook, enabled: true });
            if (saved.youtube) synth.push({ id: '4', platform: 'youtube', url: saved.youtube, enabled: true });
            if (saved.linkedin) synth.push({ id: '5', platform: 'linkedin', url: saved.linkedin, enabled: true });
            if (saved.tiktok) synth.push({ id: '6', platform: 'tiktok', url: saved.tiktok, enabled: true });
            if (saved.whatsappSocial || saved.whatsapp) synth.push({ id: '7', platform: 'whatsapp', url: saved.whatsappSocial || saved.whatsapp, enabled: true });
            if (synth.length > 0) setSocialLinks(synth);
          }
        }
      })
      .catch(err => console.warn('Could not fetch footer settings from API:', err));
  }, []);

  const handleSave = async () => {
    setIsSaved(true);
    const flatSocial: Record<string, string> = {};
    socialLinks.forEach(item => {
      if (item.enabled && item.url.trim()) {
        flatSocial[item.platform === 'x' ? 'twitter' : item.platform] = item.url.trim();
        if (item.platform === 'whatsapp') flatSocial['whatsappSocial'] = item.url.trim();
      }
    });

    const footerData = {
      newsletterTitle,
      newsletterDesc,
      newsletterConsent,
      helpLinks,
      aboutLinks,
      address,
      phone,
      whatsapp,
      email,
      hours,
      socialLinks,
      ...flatSocial,
      playStore,
      appStore,
      payments,
      copyrightText,
    };
    try {
      await AdminApiClient.updateSettings('footer_settings', footerData);
    } catch (err: any) {
      console.error('Failed to save footer settings to PostgreSQL:', err);
    }
    setTimeout(() => setIsSaved(false), 3000);
  };

  const handleAddSocialLink = () => {
    const usedPlatforms = new Set(socialLinks.map(s => s.platform));
    const nextAvail = AVAILABLE_PLATFORMS.find(p => !usedPlatforms.has(p.key)) || AVAILABLE_PLATFORMS[0];
    const newItem: SocialLinkItem = {
      id: `${Date.now()}`,
      platform: nextAvail.key,
      url: nextAvail.defaultUrl,
      enabled: true,
    };
    setSocialLinks([...socialLinks, newItem]);
  };

  const handleDeleteSocialLink = (id: string) => {
    setSocialLinks(socialLinks.filter(s => s.id !== id));
  };

  const handleUpdateSocialLink = (id: string, field: keyof SocialLinkItem, val: any) => {
    setSocialLinks(socialLinks.map(s => {
      if (s.id !== id) return s;
      if (field === 'platform') {
        const found = AVAILABLE_PLATFORMS.find(p => p.key === val);
        return {
          ...s,
          platform: val,
          url: s.url && s.url.includes(s.platform) && found ? found.defaultUrl : s.url,
        };
      }
      return { ...s, [field]: val };
    }));
  };

  const handleAddLink = (col: 'help' | 'about') => {
    const newItem: LinkItem = {
      id: `${Date.now()}`,
      label: 'New Link',
      url: '/',
    };
    if (col === 'help') setHelpLinks([...helpLinks, newItem]);
    else setAboutLinks([...aboutLinks, newItem]);
  };

  const handleDeleteLink = (col: 'help' | 'about', id: string) => {
    if (col === 'help') setHelpLinks(helpLinks.filter(l => l.id !== id));
    else setAboutLinks(aboutLinks.filter(l => l.id !== id));
  };

  const handleUpdateLink = (col: 'help' | 'about', id: string, field: 'label' | 'url', val: string) => {
    const updater = (list: LinkItem[]) => list.map(l => l.id === id ? { ...l, [field]: val } : l);
    if (col === 'help') setHelpLinks(updater(helpLinks));
    else setAboutLinks(updater(aboutLinks));
  };

  return (
    <div className="space-y-6">
      {/* Top Banner */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-5 rounded-2xl border bg-[#152a23] dark:bg-[#152a23] border-[#22493c] light:bg-white light:border-[#D5E4DC]">
        <div>
          <div className="flex items-center gap-2">
            <Globe className="w-5 h-5 text-brand-400" />
            <h4 className="text-base font-bold text-white dark:text-white light:text-[#0D1C17]">
              Universal Web/Desktop Footer & Navigation Editor
            </h4>
          </div>
          <p className="text-xs text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mt-1">
            Customize the 4-column e-commerce footer displayed across all desktop web pages, contact channels, social handles, and payment badges.
          </p>
        </div>

        <button
          onClick={handleSave}
          className="flex items-center gap-2 px-5 py-2.5 rounded-full text-xs font-bold text-forest-void bg-brand-500 hover:bg-brand-400 shadow-glow-sm transition-all shrink-0"
        >
          {isSaved ? <CheckCircle className="w-4 h-4" /> : <Save className="w-4 h-4" />}
          <span>{isSaved ? 'Settings Saved Live!' : 'Save Footer Settings'}</span>
        </button>
      </div>

      {isSaved && (
        <div className="p-3.5 rounded-xl bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 text-xs flex items-center gap-2 font-bold animate-fade-in">
          <CheckCircle className="w-4 h-4" />
          <span>Footer settings successfully synchronized with desktop web and mobile viewports!</span>
        </div>
      )}

      {/* Section 1: Newsletter Subscription Box */}
      <div className="p-5 rounded-3xl border bg-[#152a23] border-[#22493c] space-y-4">
        <h5 className="text-sm font-bold text-white flex items-center gap-2">
          <Mail className="w-4 h-4 text-brand-400" />
          <span>1. Top Newsletter Banner Strip</span>
        </h5>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-[#90cbb7] mb-1">
              Banner Headline:
            </label>
            <input
              type="text"
              value={newsletterTitle}
              onChange={(e) => setNewsletterTitle(e.target.value)}
              className="w-full p-2.5 rounded-xl text-xs outline-none border bg-[#10231d] border-[#22493c] text-white focus:border-brand-500"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-[#90cbb7] mb-1">
              Consent Checkbox Text:
            </label>
            <input
              type="text"
              value={newsletterConsent}
              onChange={(e) => setNewsletterConsent(e.target.value)}
              className="w-full p-2.5 rounded-xl text-xs outline-none border bg-[#10231d] border-[#22493c] text-white focus:border-brand-500"
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-[#90cbb7] mb-1">
            Newsletter Subtitle / Description:
          </label>
          <textarea
            value={newsletterDesc}
            onChange={(e) => setNewsletterDesc(e.target.value)}
            rows={2}
            className="w-full p-2.5 rounded-xl text-xs outline-none border bg-[#10231d] border-[#22493c] text-white focus:border-brand-500"
          />
        </div>
      </div>

      {/* Section 2: Four Columns Editor */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Column 1: Need Help */}
        <div className="p-5 rounded-3xl border bg-[#152a23] border-[#22493c] space-y-3">
          <div className="flex items-center justify-between">
            <h5 className="text-sm font-bold text-white">Column 1: Need Help Links</h5>
            <button
              onClick={() => handleAddLink('help')}
              className="p-1 rounded-lg text-brand-400 hover:bg-brand-500/15 flex items-center gap-1 text-xs"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>Add</span>
            </button>
          </div>

          <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
            {helpLinks.map((link) => (
              <div key={link.id} className="flex items-center gap-2 p-2 rounded-xl bg-[#10231d] border border-[#22493c]">
                <input
                  type="text"
                  value={link.label}
                  onChange={(e) => handleUpdateLink('help', link.id, 'label', e.target.value)}
                  className="flex-1 px-2 py-1 text-xs rounded bg-black/20 border border-white/5 text-white"
                />
                <input
                  type="text"
                  value={link.url}
                  onChange={(e) => handleUpdateLink('help', link.id, 'url', e.target.value)}
                  className="w-32 px-2 py-1 text-xs font-mono rounded bg-black/20 border border-white/5 text-[#90cbb7]"
                />
                <button
                  onClick={() => handleDeleteLink('help', link.id)}
                  className="p-1 text-rose-400 hover:bg-rose-500/15 rounded"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Column 2: About ReUseIt */}
        <div className="p-5 rounded-3xl border bg-[#152a23] border-[#22493c] space-y-3">
          <div className="flex items-center justify-between">
            <h5 className="text-sm font-bold text-white">Column 2: About ReUseIt Links</h5>
            <button
              onClick={() => handleAddLink('about')}
              className="p-1 rounded-lg text-brand-400 hover:bg-brand-500/15 flex items-center gap-1 text-xs"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>Add</span>
            </button>
          </div>

          <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
            {aboutLinks.map((link) => (
              <div key={link.id} className="flex items-center gap-2 p-2 rounded-xl bg-[#10231d] border border-[#22493c]">
                <input
                  type="text"
                  value={link.label}
                  onChange={(e) => handleUpdateLink('about', link.id, 'label', e.target.value)}
                  className="flex-1 px-2 py-1 text-xs rounded bg-black/20 border border-white/5 text-white"
                />
                <input
                  type="text"
                  value={link.url}
                  onChange={(e) => handleUpdateLink('about', link.id, 'url', e.target.value)}
                  className="w-32 px-2 py-1 text-xs font-mono rounded bg-black/20 border border-white/5 text-[#90cbb7]"
                />
                <button
                  onClick={() => handleDeleteLink('about', link.id)}
                  className="p-1 text-rose-400 hover:bg-rose-500/15 rounded"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Column 3 & 4: Contact & Social Handles */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Contact Details */}
        <div className="p-5 rounded-3xl border bg-[#152a23] border-[#22493c] space-y-3">
          <h5 className="text-sm font-bold text-white flex items-center gap-2">
            <MapPin className="w-4 h-4 text-brand-400" />
            <span>Column 3: Office Address & Contact</span>
          </h5>

          <div>
            <label className="block text-xs text-[#90cbb7] mb-1">Physical Address:</label>
            <input
              type="text"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              className="w-full p-2 rounded-xl text-xs bg-[#10231d] border border-[#22493c] text-white"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-[#90cbb7] mb-1">Phone Line:</label>
              <input
                type="text"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full p-2 rounded-xl text-xs bg-[#10231d] border border-[#22493c] text-white"
              />
            </div>
            <div>
              <label className="block text-xs text-[#90cbb7] mb-1">WhatsApp Hotline:</label>
              <input
                type="text"
                value={whatsapp}
                onChange={(e) => setWhatsapp(e.target.value)}
                className="w-full p-2 rounded-xl text-xs bg-[#10231d] border border-[#22493c] text-white"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-[#90cbb7] mb-1">Email:</label>
              <input
                type="text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full p-2 rounded-xl text-xs bg-[#10231d] border border-[#22493c] text-white"
              />
            </div>
            <div>
              <label className="block text-xs text-[#90cbb7] mb-1">Hours:</label>
              <input
                type="text"
                value={hours}
                onChange={(e) => setHours(e.target.value)}
                className="w-full p-2 rounded-xl text-xs bg-[#10231d] border border-[#22493c] text-white"
              />
            </div>
          </div>
        </div>

        {/* Social Media & App Stores */}
        <div className="p-5 rounded-3xl border bg-[#152a23] border-[#22493c] space-y-4">
          <div className="flex items-center justify-between">
            <h5 className="text-sm font-bold text-white flex items-center gap-2">
              <Share2 className="w-4 h-4 text-brand-400" />
              <span>Column 4: Social Media Icons ({socialLinks.filter(s => s.enabled).length} Active)</span>
            </h5>
            <button
              onClick={handleAddSocialLink}
              disabled={socialLinks.length >= AVAILABLE_PLATFORMS.length}
              className={`px-3 py-1.5 rounded-xl flex items-center gap-1.5 text-xs font-bold transition-all ${
                socialLinks.length >= AVAILABLE_PLATFORMS.length
                  ? 'bg-white/5 text-white/30 cursor-not-allowed'
                  : 'bg-brand-500/20 text-brand-300 hover:bg-brand-500/30 border border-brand-500/30'
              }`}
            >
              <Plus className="w-3.5 h-3.5" />
              <span>Add Icon</span>
            </button>
          </div>

          <p className="text-[11px] text-[#90cbb7]/80">
            Add or remove brand icons and adjust URLs. Only enabled icons will appear in a single line on the desktop footer.
          </p>

          <div className="space-y-2.5 max-h-80 overflow-y-auto pr-1">
            {socialLinks.map((item) => {
              const platformMeta = AVAILABLE_PLATFORMS.find(p => p.key === item.platform) || AVAILABLE_PLATFORMS[0];
              return (
                <div
                  key={item.id}
                  className={`flex items-center gap-2 p-2 rounded-xl border transition-all ${
                    item.enabled
                      ? 'bg-[#10231d] border-[#22493c]'
                      : 'bg-[#10231d]/50 border-white/5 opacity-60'
                  }`}
                >
                  <label className="flex items-center cursor-pointer p-1" title={item.enabled ? 'Enabled' : 'Disabled'}>
                    <input
                      type="checkbox"
                      checked={item.enabled}
                      onChange={(e) => handleUpdateSocialLink(item.id, 'enabled', e.target.checked)}
                      className="w-4 h-4 rounded accent-brand-500 cursor-pointer"
                    />
                  </label>

                  <select
                    value={item.platform}
                    onChange={(e) => handleUpdateSocialLink(item.id, 'platform', e.target.value)}
                    className="px-2 py-1 text-xs rounded bg-black/40 border border-white/10 text-white font-semibold outline-none focus:border-brand-500"
                  >
                    {AVAILABLE_PLATFORMS.map((p) => (
                      <option key={p.key} value={p.key} className="bg-[#10231d] text-white">
                        {p.label}
                      </option>
                    ))}
                  </select>

                  <input
                    type="text"
                    value={item.url}
                    placeholder={`Enter ${platformMeta.label} URL...`}
                    onChange={(e) => handleUpdateSocialLink(item.id, 'url', e.target.value)}
                    className="flex-1 px-2.5 py-1 text-xs rounded bg-black/20 border border-white/5 text-[#90cbb7] font-mono outline-none focus:border-brand-500"
                  />

                  <button
                    onClick={() => handleDeleteSocialLink(item.id)}
                    title={`Remove ${platformMeta.label} icon`}
                    className="p-1.5 text-rose-400 hover:bg-rose-500/15 rounded transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              );
            })}

            {socialLinks.length === 0 && (
              <div className="p-4 text-center text-xs text-white/50 border border-dashed border-[#22493c] rounded-xl">
                No social media icons configured. Click "+ Add Icon" above to add one.
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3 pt-2 border-t border-[#22493c]/60">
            <div>
              <label className="block text-xs text-[#90cbb7] mb-1">Google Play URL:</label>
              <input
                type="text"
                value={playStore}
                onChange={(e) => setPlayStore(e.target.value)}
                className="w-full p-2 rounded-xl text-xs bg-[#10231d] border border-[#22493c] text-white"
              />
            </div>
            <div>
              <label className="block text-xs text-[#90cbb7] mb-1">iOS App Store URL:</label>
              <input
                type="text"
                value={appStore}
                onChange={(e) => setAppStore(e.target.value)}
                className="w-full p-2 rounded-xl text-xs bg-[#10231d] border border-[#22493c] text-white"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Section 3: Payment Methods & Copyright Strip */}
      <div className="p-5 rounded-3xl border bg-[#152a23] border-[#22493c] space-y-4">
        <h5 className="text-sm font-bold text-white flex items-center gap-2">
          <CreditCard className="w-4 h-4 text-brand-400" />
          <span>Accepted Payment Methods & Legal Disclaimer</span>
        </h5>

        <div className="flex flex-wrap items-center gap-3">
          {Object.keys(payments).map((key) => (
            <label
              key={key}
              className={`px-3.5 py-2 rounded-xl border text-xs font-semibold cursor-pointer flex items-center gap-2 transition-all ${
                payments[key]
                  ? 'bg-brand-500/20 border-brand-500 text-brand-300'
                  : 'bg-[#10231d] border-[#22493c] text-slate-400'
              }`}
            >
              <input
                type="checkbox"
                checked={payments[key]}
                onChange={(e) => setPayments({ ...payments, [key]: e.target.checked })}
                className="w-3.5 h-3.5 accent-brand-500"
              />
              <span>{key}</span>
            </label>
          ))}
        </div>

        <div>
          <label className="block text-xs font-semibold text-[#90cbb7] mb-1">
            Copyright & Security Guarantee Text:
          </label>
          <input
            type="text"
            value={copyrightText}
            onChange={(e) => setCopyrightText(e.target.value)}
            className="w-full p-2.5 rounded-xl text-xs outline-none border bg-[#10231d] border-[#22493c] text-white focus:border-brand-500"
          />
        </div>
      </div>
    </div>
  );
};
